<!-- /* * Confidential and Proprietary. * Do not distribute without 1-800-Flowers.com, Inc. consent. *
Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved. */ -->

{#if !loading}
	<ChatWrapper {restartConversation} {clickedSuggestions} {clickedOrders} {showModal} {modalId} />
{/if}

<script lang="ts">
	import { onMount } from 'svelte';
	import {
		chatSession,
		reInitializeChat,
		entryMessage,
		chatHeaderButtonClicked,
		updateSessionStorage,
		chatTriggeredExternally,
		chatVisible,
		notificationSoundEnabled,
		chatPreviewMsgVerbiage,
		connectedToAgent,
		persistentSessionData,
		displayDisconnectWarning,
		displayDisconnect
	} from '$lib/stores/chatSessionStore';
	import ChatWrapper from '$lib/components/chat/ChatWrapper.svelte';
	import { getChannel } from '$lib/utilities/getChannel';
	import { sendMessage } from '$lib/apis/webchat/sendMessage';
	import { soundNotification } from '$lib/utilities/soundNotification';
	import { pwaIframeEvent } from '../../utilities/pwaIframeEvent';
	import { getAllFlagKeys } from '$lib/apis/bumblebee/getFeatureFlags';
	import { writable } from 'svelte/store';
	import { cleanUpStores } from '../../utilities/endConversation';
	import { getDeviceType, getOS } from '$lib/utilities/getBroswerData';
	import { floraChatEventTracking } from '../../utilities/analytics';

	let showModal = writable(false);
	let modalId = writable('');
	$: currentChatSession = $chatSession;
	$: shouldDisplayDisconnect = $displayDisconnect;

	$: if (currentChatSession) {
		chatSession.update((cs) => ({ ...cs, brand: currentChatSession?.brand }));
	}
	$: if ($displayDisconnectWarning) {
		modalId.set('disconnect_warning');
		displayDisconnectWarning.set(false);
		showModal.set(true);
	}
	$: if (shouldDisplayDisconnect) {
		modalId.set('disconnect');
		displayDisconnect.set(false);
		showModal.set(true);
	}
	export let clickedSuggestions: { [key: string]: boolean } = {};
	export let clickedOrders: { [key: string]: string };

	let loading = true;
	const { play } = soundNotification('./ding.mp3');

	const runStartConversation = async () => {
		chatSession.update((cs) => ({ ...cs, brand: currentChatSession?.brand }));
		chatSession.update((old) => {
			const updatedSession = {
				...old,
				brand: currentChatSession.brand,
				enterpriseId: currentChatSession?.enterpriseId,
				channel: currentChatSession.channel
			};
			updateSessionStorage(updatedSession);
			return updatedSession;
		});
		await updateSessionStorage(currentChatSession);
	};

	const handleSessionUpdate = (sessionData: Record<string, any>) => {
		if (sessionData) {
			chatSession.update((old) => {
				const updatedSession = {
					...old,
					...(sessionData.auth && {
						auth: sessionData?.auth?.isAuthenticated
					}),
					...(sessionData.profile && {
						firstName: sessionData?.profile?.firstName,
						lastName: sessionData?.profile?.lastName,
						email: sessionData?.profile?.email,
						contactId: sessionData?.profile?.contactId,
						loyaltyStatus: sessionData?.profile?.loyaltyStatus,
						passportEndDate: sessionData?.profile?.passportEndDate
					}),
					token: sessionData?.token || sessionData?.auth?.accessToken || '',
					brand: sessionData?.brand?.code,
					enterpriseId: sessionData?.enterprise?.enterpriseId,
					browseruuid: sessionData?.browseruuid,
					deviceType: sessionData?.deviceType,
					operatingSystem: sessionData?.operatingSystem,
					channel: getChannel(sessionData?.deviceType)
				};
				updateSessionStorage(updatedSession);
				return updatedSession;
			});
		}
	};

	const setupConversation = async () => {
		reInitializeChat.set(false);
		window.parent.postMessage(
			{ type: 'flora-pwa-events-logic', data: JSON.stringify(pwaIframeEvent) },
			'*'
		);
		window.parent.postMessage(
			{ type: 'pwa-events-logic', data: JSON.stringify(pwaIframeEvent) },
			'*'
		);
		window.parent.postMessage({ type: 'flora-conv-ui-session-info' }, '*');
		const data = sessionStorage.getItem('conversation');
		const handleMessage = async (event: MessageEvent) => {
			const type = event?.data?.type;
			if (type === 'flora-session-storage-data') {
				const sessionData = event.data?.data;
				handleSessionUpdate(sessionData);
				if ($chatSession.token && !Object.keys($chatSession.flags || {})?.length) {
					// set the conversational flags
					const allFeatureFlags = await getAllFlagKeys({
						brand_code: $chatSession.brand,
						channel: $chatSession.channel,
						email: $chatSession.email || 'noemail',
						browser_uuid: $chatSession.browseruuid,
						token: $chatSession.token
					});
					chatSession.update((cs) => ({ ...cs, flags: allFeatureFlags }));
					persistentSessionData.set({
						operatingSystem: $chatSession?.operatingSystem,
						allowedDeviceTypes: $chatSession?.flags?.['flora-mobile-experience-enabled'] as string[]
					});
					chatPreviewMsgVerbiage.set($chatSession.flags['flora-preview-message-verbiage']);
				}
			} else if (type === 'flora-chat-header-button') {
				chatSession.update((cs) => ({ ...cs, entry_point: 'header' }));
				updateSessionStorage({ ...$chatSession, entry_point: 'header' });
				chatHeaderButtonClicked.set(true);
			} else if (type === 'flora-update-chat-message-search') {
				let sessionStorageObject = { entry_point: '' };
				try {
					sessionStorageObject = JSON.parse((data as string) || '{}');
				} catch (error) {
					console.error('Error parsing conversation data:', error);
				}
				//This is to handle occurence where we saw this event firing off more then once
				if (sessionStorageObject?.entry_point === 'search') return;
				chatSession.update((cs) => ({ ...cs, entry_point: 'search' }));
				updateSessionStorage({ ...$chatSession, entry_point: 'search' });
				if (event?.data?.data?.message?.suggestion) {
					entryMessage.set(event?.data?.data?.message);
					sessionStorage.setItem('entryMessage', JSON.stringify($entryMessage));
					if ($chatSession.messages.length > 1 && $entryMessage) {
						// This is to handle someone minimizing chat and engaging the search bar again
						chatSession.update((old) => {
							const updatedSession = {
								...old,
								messages: [
									...old.messages,
									{ sender: 'customer', message: $entryMessage.suggestion, type: 'text' }
								],
								chatHistory: returnValue?.params.conversation_history || old.chatHistory
							};
							sessionStorage.setItem('conversation', JSON.stringify(updatedSession));
							return updatedSession;
						});
						const returnValue = await sendMessage(
							{
								conversation_id: $chatSession.conversation_id,
								brand_code: $chatSession.brand,
								channel: $chatSession.channel,
								message: $entryMessage.suggestion,
								type: 'text',
								params: {
									typing: false,
									passport: {
										passportStatus: !!$chatSession?.passportEndDate
									},
									customer: {
										email: $chatSession.email,
										contact_id: $chatSession.contactId,
										browser_uuid: $chatSession.browseruuid,
										first: $chatSession.firstName,
										last: $chatSession.lastName
									},
									selected_option: {
										type: 'suggestions',
										suggestions: $entryMessage
									}
								}
							},
							$chatSession?.token
						);
						sessionStorage.removeItem('entryMessage');
						entryMessage.set({ suggestion: '', type: '' });
						chatSession.update((old) => {
							const updatedSession = {
								...old,
								messages: [
									...old.messages,
									{
										sender: 'bot',
										message: returnValue?.message,
										type: returnValue?.type,
										params: returnValue?.params || {}
									}
								],
								chatHistory: returnValue?.params.conversation_history || old.chatHistory
							};
							sessionStorage.setItem('conversation', JSON.stringify(updatedSession));
							return updatedSession;
						});
						if ($notificationSoundEnabled) play();
					}
				}
			} else if (type === 'flora-update-chat-message-footer') {
				chatSession.update((cs) => ({ ...cs, entry_point: 'footer' }));
				updateSessionStorage({ ...$chatSession, entry_point: 'footer' });
				entryMessage.set(event?.data?.data?.message);
				sessionStorage.setItem('entryMessage', JSON.stringify($entryMessage.suggestion));
			} else if (type === 'flora-chat-iframe-maximised') {
				window.parent.postMessage({ type: 'flora-chat-iframe-maximised' }, '*');
			} else if (type === 'flora-update-page-info') {
				floraChatEventTracking(
					$chatSession.conversation_id || 'N/A',
					'pageType',
					event?.data?.data || ''
				);
				chatSession.update((cs) => ({ ...cs, entry_page_type: event.data.data }));
				updateSessionStorage({ ...$chatSession, entry_page_type: event.data.data });
			} else if (type === 'flora-update-parent-viewport-height') {
				chatSession.update((cs) => ({ ...cs, parentViewPortHeight: event.data.data }));
				updateSessionStorage({ ...$chatSession, parentViewPortHeight: event.data.data });
			} else if (type === 'flora-update-access-token') {
				if (event.data.data) {
					chatSession.update((cs) => ({ ...cs, token: event.data.data }));
					updateSessionStorage({ ...$chatSession, token: event.data.data });
				}
			} else if (type === 'flora-update-cart-passport-status') {
				chatSession.update((cs) => ({
					...cs,
					cartOrderDetails: {
						...$chatSession.cartOrderDetails,
						cartHasPassport: event.data.data.cartHasPassport
					}
				}));
				updateSessionStorage({
					...$chatSession,
					cartOrderDetails: {
						...$chatSession.cartOrderDetails,
						cartHasPassport: event.data.data.cartHasPassport
					}
				});
			} else if (type === 'flora-update-visiting-page') {
				chatSession.update((cs) => ({ ...cs, currentUrl: event.data.data.currentUrl }));
				updateSessionStorage({ ...$chatSession, currentUrl: event.data.data.currentUrl });
			} else if (type === 'flora-update-cart-details') {
				chatSession.update((cs) => ({ ...cs, cartOrderDetails: { ...event.data.data } }));
				updateSessionStorage({
					...$chatSession,
					cartOrderDetails: {
						...$chatSession.cartOrderDetails,
						isItemAdded: event.data.data.isItemAdded,
						orderValue: event.data.data.orderValue,
						orderNumber: event.data.data.orderNumber
					}
				});
			} else if (type === 'flora-update-order-placed-status') {
				chatSession.update((cs) => ({
					...cs,
					cartOrderDetails: {
						...$chatSession.cartOrderDetails,
						wasOrderPlaced: event.data.data.wasOrderPlaced,
						orderNumber: event.data.data.orderNumber
					}
				}));
				updateSessionStorage({ ...$chatSession, cartOrderDetails: { ...event.data.data } });
			} else if (type === 'trigger-flora-chat') {
				if (!$chatVisible) chatTriggeredExternally.set(true);
			}
		};
		window.removeEventListener('message', handleMessage);
		window.addEventListener('message', handleMessage);

		try {
			const chatData = JSON.parse((data as string) || '{}');
			if (chatData?.conversation_id) {
				chatSession.update(() => chatData);
			} else {
				await runStartConversation();
			}
		} catch (error) {
			console.error('Error parsing conversation data:', error);
		} finally {
			loading = false;
		}

		window.addEventListener('beforeunload', () => {
			// check and see if we are connected to an agent
			if ($connectedToAgent) {
				// run the end conversation logic to clear our state because we do NOT want to persist the session.
				cleanUpStores();
			}
		});
		return () => {
			window.removeEventListener('message', handleMessage);
		};
	};

	onMount(() => {
		const deviceType = getDeviceType();
		const operatingSystem = getOS();
		const channel = getChannel(deviceType);
		chatSession.update((cs) => ({ ...cs, deviceType, operatingSystem, channel }));
		const mount = async () => {
			await setupConversation();
		};
		mount();
	});

	export const restartConversation = async () => {
		clickedSuggestions = {};
		clickedOrders = {};
		await setupConversation();
	};
</script>
